import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Save, Loader2, Plus, Trash2, GripVertical,
  Users, Type, Mail, Phone, FileText, List, ToggleLeft,
  Sparkles, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import api from '../../lib/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Texto corto', icon: Type },
  { value: 'textarea', label: 'Texto largo', icon: FileText },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Teléfono', icon: Phone },
  { value: 'select', label: 'Selección', icon: List },
  { value: 'checkbox', label: 'Checkbox', icon: ToggleLeft },
];

const SortableField = ({ field, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
    >
      <div className="flex items-start gap-3">
        <button
          {...attributes}
          {...listeners}
          className="mt-2 text-zinc-500 hover:text-zinc-300 cursor-grab active:cursor-grabbing"
        >
          <GripVertical size={20} />
        </button>
        
        <div className="flex-1 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Etiqueta</Label>
              <Input
                value={field.label}
                onChange={(e) => onUpdate(field.id, 'label', e.target.value)}
                className="bg-zinc-800 border-zinc-700 mt-1"
                placeholder="Nombre del campo"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Tipo</Label>
              <select
                value={field.type}
                onChange={(e) => onUpdate(field.id, 'type', e.target.value)}
                className="w-full mt-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
              >
                {FIELD_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Placeholder</Label>
              <Input
                value={field.placeholder || ''}
                onChange={(e) => onUpdate(field.id, 'placeholder', e.target.value)}
                className="bg-zinc-800 border-zinc-700 mt-1"
                placeholder="Texto de ayuda"
              />
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(e) => onUpdate(field.id, 'required', e.target.checked)}
                  className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-[#D4AF37]"
                />
                <span className="text-sm text-zinc-400">Obligatorio</span>
              </label>
            </div>
          </div>
          
          {field.type === 'select' && (
            <div>
              <Label className="text-xs text-zinc-500">Opciones (una por línea)</Label>
              <Textarea
                value={field.options?.join('\n') || ''}
                onChange={(e) => onUpdate(field.id, 'options', e.target.value.split('\n').filter(o => o.trim()))}
                className="bg-zinc-800 border-zinc-700 mt-1"
                rows={3}
                placeholder="Opción 1&#10;Opción 2&#10;Opción 3"
              />
            </div>
          )}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(field.id)}
          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          <Trash2 size={18} />
        </Button>
      </div>
    </div>
  );
};

const ProducerFormEditorPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formConfig, setFormConfig] = useState({
    title: 'Quiero ser Productora',
    description: 'Completa el formulario para registrar tu productora',
    fields: [],
    // Hero section customization
    hero: {
      badge: 'Portal de Productoras',
      title: 'Vendé tus entradas con nosotros',
      subtitle: 'Registrá tu productora y accedé a nuestra plataforma de venta de entradas, estadísticas en tiempo real y una comunidad de compradores activos.'
    },
    // Benefits section
    benefits: [
      { title: 'Gestión Completa', description: 'Panel propio para crear y gestionar eventos' },
      { title: 'Estadísticas', description: 'Métricas de ventas y comunidad en tiempo real' },
      { title: 'Tu Marca', description: 'Personalización con tu logo, colores y dominio' }
    ],
    // Show/hide sections
    showBenefits: true
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadFormConfig();
  }, []);

  const loadFormConfig = async () => {
    try {
      const response = await api.get('/settings/form-producer');
      if (response.data) {
        // Merge with defaults to ensure all fields exist
        setFormConfig(prev => ({
          ...prev,
          ...response.data,
          hero: {
            badge: 'Portal de Productoras',
            title: 'Vendé tus entradas con nosotros',
            subtitle: 'Registrá tu productora y accedé a nuestra plataforma de venta de entradas, estadísticas en tiempo real y una comunidad de compradores activos.',
            ...response.data.hero
          },
          benefits: response.data.benefits || [
            { title: 'Gestión Completa', description: 'Panel propio para crear y gestionar eventos' },
            { title: 'Estadísticas', description: 'Métricas de ventas y comunidad en tiempo real' },
            { title: 'Tu Marca', description: 'Personalización con tu logo, colores y dominio' }
          ],
          showBenefits: response.data.showBenefits !== false
        }));
      }
    } catch (error) {
      // If not found, use defaults
      setFormConfig({
        title: 'Registrar Productora',
        description: 'Completa el formulario para registrar tu productora y comenzar a crear eventos en la plataforma BRAVO.',
        hero: {
          badge: 'Portal de Productoras',
          title: 'Vendé tus entradas con nosotros',
          subtitle: 'Registrá tu productora y accedé a nuestra plataforma de venta de entradas, estadísticas en tiempo real y una comunidad de compradores activos.'
        },
        benefits: [
          { title: 'Gestión Completa', description: 'Panel propio para crear y gestionar eventos' },
          { title: 'Estadísticas', description: 'Métricas de ventas y comunidad en tiempo real' },
          { title: 'Tu Marca', description: 'Personalización con tu logo, colores y dominio' }
        ],
        showBenefits: true,
        fields: [
          { id: 'field_1', label: 'Nombre de la productora', type: 'text', required: true, placeholder: 'Ej: Eventos XYZ' },
          { id: 'field_2', label: 'Tipo de eventos', type: 'select', required: true, options: ['Fiestas', 'Conciertos', 'Festivales', 'Eventos corporativos', 'Mixto'] },
          { id: 'field_3', label: 'Años de experiencia', type: 'select', required: true, options: ['Menos de 1 año', '1-3 años', '3-5 años', 'Más de 5 años'] },
          { id: 'field_4', label: 'Nombre del responsable', type: 'text', required: true, placeholder: 'Nombre completo' },
          { id: 'field_5', label: 'Email de contacto', type: 'email', required: true, placeholder: 'email@productora.com' },
          { id: 'field_6', label: 'Teléfono', type: 'phone', required: true, placeholder: '+54 11 1234-5678' },
          { id: 'field_7', label: 'Descripción de la productora', type: 'textarea', required: true, placeholder: 'Cuéntanos sobre tu productora, tipo de eventos que organizan, etc.' },
          { id: 'field_8', label: 'Redes sociales', type: 'text', required: false, placeholder: '@instagram' },
          { id: 'field_9', label: 'Sitio web', type: 'text', required: false, placeholder: 'https://...' },
          { id: 'field_10', label: 'Referencias o eventos anteriores', type: 'textarea', required: false, placeholder: 'Menciona eventos destacados que hayas organizado' },
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/form-producer', formConfig);
      toast.success('Formulario guardado');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      label: 'Nuevo campo',
      type: 'text',
      required: false,
      placeholder: ''
    };
    setFormConfig(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const updateField = (id, key, value) => {
    setFormConfig(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === id ? { ...f, [key]: value } : f)
    }));
  };

  const deleteField = (id) => {
    setFormConfig(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== id)
    }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setFormConfig(prev => {
        const oldIndex = prev.fields.findIndex(f => f.id === active.id);
        const newIndex = prev.fields.findIndex(f => f.id === over.id);
        return {
          ...prev,
          fields: arrayMove(prev.fields, oldIndex, newIndex)
        };
      });
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center min-h-screen">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="producer-form-editor">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Link 
                to="/admin/formularios" 
                className="inline-flex items-center text-zinc-400 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft size={16} className="mr-2" />
                Volver a Formularios
              </Link>
              <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                <Users className="text-[#D4AF37]" />
                Formulario de Productora
              </h1>
              <p className="text-zinc-500 text-sm mt-1">
                Configura los campos del formulario de registro
              </p>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
              Guardar
            </Button>
          </div>

          {/* Form Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Hero Section Customization */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="text-[#D4AF37]" size={20} />
                <h2 className="font-bold text-white">Sección Superior (Hero)</h2>
              </div>
              <p className="text-zinc-500 text-sm mb-4">Personaliza el encabezado que ven los usuarios al entrar al formulario</p>
              
              <div>
                <Label className="text-zinc-400">Badge (etiqueta superior)</Label>
                <Input
                  value={formConfig.hero?.badge || ''}
                  onChange={(e) => setFormConfig(prev => ({ 
                    ...prev, 
                    hero: { ...prev.hero, badge: e.target.value } 
                  }))}
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  placeholder="Ej: Portal de Productoras"
                />
              </div>
              
              <div>
                <Label className="text-zinc-400">Título Principal</Label>
                <Input
                  value={formConfig.hero?.title || ''}
                  onChange={(e) => setFormConfig(prev => ({ 
                    ...prev, 
                    hero: { ...prev.hero, title: e.target.value } 
                  }))}
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  placeholder="Ej: Vendé tus entradas con nosotros"
                />
              </div>
              
              <div>
                <Label className="text-zinc-400">Subtítulo / Descripción</Label>
                <Textarea
                  value={formConfig.hero?.subtitle || ''}
                  onChange={(e) => setFormConfig(prev => ({ 
                    ...prev, 
                    hero: { ...prev.hero, subtitle: e.target.value } 
                  }))}
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  rows={3}
                  placeholder="Describe brevemente los beneficios de registrarse"
                />
              </div>
            </div>

            {/* Benefits Section */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <List className="text-[#D4AF37]" size={20} />
                  <h2 className="font-bold text-white">Beneficios Destacados</h2>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formConfig.showBenefits !== false}
                    onChange={(e) => setFormConfig(prev => ({ ...prev, showBenefits: e.target.checked }))}
                    className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-[#D4AF37]"
                  />
                  <span className="text-sm text-zinc-400">Mostrar sección</span>
                </label>
              </div>
              <p className="text-zinc-500 text-sm mb-4">Las 3 tarjetas que aparecen debajo del título principal</p>
              
              {formConfig.showBenefits !== false && (
                <div className="grid gap-4">
                  {(formConfig.benefits || []).map((benefit, index) => (
                    <div key={index} className="bg-zinc-800 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-zinc-500 uppercase">Beneficio {index + 1}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-zinc-500">Título</Label>
                          <Input
                            value={benefit.title}
                            onChange={(e) => {
                              const newBenefits = [...(formConfig.benefits || [])];
                              newBenefits[index] = { ...newBenefits[index], title: e.target.value };
                              setFormConfig(prev => ({ ...prev, benefits: newBenefits }));
                            }}
                            className="bg-zinc-700 border-zinc-600 mt-1"
                            placeholder="Título del beneficio"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-zinc-500">Descripción</Label>
                          <Input
                            value={benefit.description}
                            onChange={(e) => {
                              const newBenefits = [...(formConfig.benefits || [])];
                              newBenefits[index] = { ...newBenefits[index], description: e.target.value };
                              setFormConfig(prev => ({ ...prev, benefits: newBenefits }));
                            }}
                            className="bg-zinc-700 border-zinc-600 mt-1"
                            placeholder="Descripción breve"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Basic Info */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
              <h2 className="font-bold text-white">Título del Formulario</h2>
              <p className="text-zinc-500 text-sm">Aparece arriba de los campos del cuestionario</p>
              <div>
                <Label className="text-zinc-400">Título</Label>
                <Input
                  value={formConfig.title}
                  onChange={(e) => setFormConfig(prev => ({ ...prev, title: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Descripción</Label>
                <Textarea
                  value={formConfig.description}
                  onChange={(e) => setFormConfig(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  rows={3}
                />
              </div>
            </div>

            {/* Fields */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-white">Campos del Formulario</h2>
                <Button
                  onClick={addField}
                  variant="outline"
                  className="border-zinc-700"
                >
                  <Plus size={16} className="mr-2" />
                  Añadir Campo
                </Button>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={formConfig.fields.map(f => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {formConfig.fields.map((field) => (
                      <SortableField
                        key={field.id}
                        field={field}
                        onUpdate={updateField}
                        onDelete={deleteField}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {formConfig.fields.length === 0 && (
                <div className="text-center py-12 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl">
                  <FileText size={48} className="mx-auto mb-4 text-zinc-700" />
                  <p>No hay campos configurados</p>
                  <p className="text-sm">Añade campos para construir el formulario</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default ProducerFormEditorPage;
